import RichTextMessageEditor from '@/components/editor/lexical/RichTextMessageEditor';
import { Modal } from '@/components/modal/modal';
import { RichTextContent } from '@/components/ui/RichTextContent';
import {
  usePublishBrandTerms,
  useSaveBrandTermsDraft,
} from '@/hooks/actions/use-brand-terms-actions';
import { useFeatureFlagGate } from '@/hooks/use-feature-flag-gate';
import { useBrandTermsQuery } from '@/hooks/queries/use-brand-terms-query';
import {
  Alert,
  Badge,
  Button,
  HStack,
  Input,
  NativeSelect,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { Brand, BrandTermDocument, LexicalContent, PayloadLocale } from '@fluenceur/types';
import { FeatureFlag } from '@fluenceur/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from './SettingsSection';
import { formatSettingsDate } from './format-settings-date';

const EMPTY_CONTENT: LexicalContent = {
  root: {
    type: 'root',
    children: [
      { type: 'paragraph', version: 1, children: [{ type: 'text', version: 1, text: '' }] },
    ],
    direction: 'ltr',
    format: '',
    indent: 0,
    version: 1,
  },
};

const supportedLocale = (language: string): PayloadLocale => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('it')) return 'it';
  return 'en';
};

export const BrandTermsSection = ({ brand }: { brand: Brand }) => {
  const { t, i18n } = useTranslation();
  const gate = useFeatureFlagGate(FeatureFlag.BRAND_TERMS);
  const [locale, setLocale] = useState<PayloadLocale>(() =>
    supportedLocale(brand.settings?.preferredLanguage ?? i18n.language)
  );
  const query = useBrandTermsQuery(brand.id, locale, gate === 'granted');
  const save = useSaveBrandTermsDraft(brand.id, locale);
  const publish = usePublishBrandTerms(brand.id, locale);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<LexicalContent>(EMPTY_CONTENT);
  const [editorKey, setEditorKey] = useState('new:en');
  const [savedDraft, setSavedDraft] = useState<BrandTermDocument | null>(null);
  const [dirty, setDirty] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    const source =
      query.data.draft ?? (query.data.active?.locale === locale ? query.data.active : null);
    setTitle(source?.title ?? '');
    setContent(source?.content ?? EMPTY_CONTENT);
    setSavedDraft(query.data.draft);
    setDirty(false);
    setEditorKey(`${source?.id ?? 'new'}:${source?.contentHash ?? locale}`);
  }, [query.data, locale]);

  const handleChange = useCallback((change: { editorState: { toJSON: () => unknown } }) => {
    setContent(change.editorState.toJSON() as LexicalContent);
    setDirty(true);
  }, []);

  const preview = useMemo(() => <RichTextContent data={content} />, [content]);

  if (gate === 'denied') return null;
  if (gate === 'loading') return <Skeleton height="160px" />;

  const canManage = query.data?.canManage ?? false;
  const active = query.data?.active ?? null;
  const history = query.data?.history ?? [];
  const displayedDocument = savedDraft ?? active;

  const saveDraft = async () => {
    try {
      const draft = await save.mutateAsync({ title, locale, content });
      setSavedDraft(draft);
      setDirty(false);
    } catch {
      // The mutation owns the translated toast. Local fields deliberately stay
      // untouched so Retry submits the exact same draft.
    }
  };

  const publishDraft = async () => {
    if (!savedDraft || dirty) return;
    try {
      await publish.mutateAsync({
        draftId: savedDraft.id,
        expectedContentHash: savedDraft.contentHash,
      });
      setSavedDraft(null);
    } catch {
      // Keep the confirmed draft selected for an explicit retry.
    }
  };

  return (
    <SettingsSection
      title={t('settings.brandTerms.title')}
      description={t('settings.brandTerms.description')}
      layout="stacked"
      isLast
    >
      {query.isLoading ? (
        <VStack align="stretch" gap={3}>
          <Skeleton height="40px" />
          <Skeleton height="240px" />
        </VStack>
      ) : query.isError ? (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{t('settings.brandTerms.loadErrorTitle')}</Alert.Title>
            <Alert.Description>{t('settings.brandTerms.loadErrorDescription')}</Alert.Description>
          </Alert.Content>
          <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert.Root>
      ) : (
        <VStack align="stretch" gap={6}>
          <HStack justify="space-between" align="start" flexWrap="wrap">
            <VStack align="start" gap={1}>
              <HStack>
                <Badge colorPalette={active ? 'green' : 'gray'}>
                  {active
                    ? t('settings.brandTerms.activeStatus')
                    : t('settings.brandTerms.noActiveStatus')}
                </Badge>
                {active && (
                  <Text fontSize="sm">
                    {t('settings.brandTerms.version', { version: active.version })}
                  </Text>
                )}
              </HStack>
              {active?.publishedAt && (
                <Text fontSize="sm" color="fg.muted">
                  {t('settings.brandTerms.publishedAt', {
                    date: formatSettingsDate(active.publishedAt, locale),
                  })}
                </Text>
              )}
            </VStack>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsPreviewing(false);
                  setIsEditorOpen(true);
                }}
              >
                {t('common.edit')}
              </Button>
            )}
          </HStack>

          {!canManage && (
            <Alert.Root status="info">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>{t('settings.brandTerms.readOnlyTitle')}</Alert.Title>
                <Alert.Description>
                  {t('settings.brandTerms.readOnlyDescription')}
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}

          {displayedDocument && (
            <HStack justify="space-between" borderWidth="1px" rounded="md" p={4} gap={4}>
              <VStack align="start" gap={1} minW={0}>
                <Text fontWeight="semibold" lineClamp={1}>
                  {displayedDocument.title}
                </Text>
                <Text color="fg.muted" fontSize="sm">
                  {displayedDocument.locale.toUpperCase()} ·{' '}
                  {t('settings.brandTerms.version', { version: displayedDocument.version })}
                </Text>
              </VStack>
              <Button
                size="sm"
                variant="ghost"
                flexShrink={0}
                onClick={() => {
                  setIsPreviewing(true);
                  setIsEditorOpen(true);
                }}
              >
                {t('settings.brandTerms.preview')}
              </Button>
            </HStack>
          )}

          <VStack align="stretch" gap={2}>
            <Text fontWeight="semibold">{t('settings.brandTerms.history')}</Text>
            {history.length === 0 ? (
              <Text color="fg.muted" fontSize="sm">
                {t('settings.brandTerms.noHistory')}
              </Text>
            ) : (
              history.map((term) => (
                <HStack key={term.id} justify="space-between" borderWidth="1px" rounded="md" p={3}>
                  <Text>{term.title}</Text>
                  <Text color="fg.muted" fontSize="sm">
                    {t('settings.brandTerms.version', { version: term.version })}
                    {term.publishedAt ? ` · ${formatSettingsDate(term.publishedAt, locale)}` : ''}
                  </Text>
                </HStack>
              ))
            )}
          </VStack>

          {isEditorOpen && (
            <Modal
              isOpen
              onClose={() => setIsEditorOpen(false)}
              title={t('settings.brandTerms.title')}
              description={t('settings.brandTerms.description')}
              maxWidth="960px"
              size="cover"
              dialogContentProps={{ maxH: '90dvh' }}
              bodyProps={{ overflowY: 'auto', pb: 6 }}
            >
              {canManage ? (
                <VStack align="stretch" gap={4}>
                  <NativeSelect.Root width="120px" size="sm">
                    <NativeSelect.Field
                      aria-label={t('settings.brandTerms.locale')}
                      value={locale}
                      onChange={(event) => setLocale(event.target.value as PayloadLocale)}
                    >
                      <option value="en">EN</option>
                      <option value="fr">FR</option>
                      <option value="it">IT</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <Input
                    value={title}
                    onChange={(event) => {
                      setTitle(event.target.value);
                      setDirty(true);
                    }}
                    placeholder={t('settings.brandTerms.titlePlaceholder')}
                    aria-label={t('settings.brandTerms.documentTitle')}
                  />
                  <RichTextMessageEditor
                    editorKey={editorKey}
                    namespace={`brand-terms:${brand.id}:${locale}`}
                    initialContent={JSON.stringify(content)}
                    availableVariables={[]}
                    onContentChange={handleChange}
                    placeholder={t('settings.brandTerms.contentPlaceholder')}
                    ariaLabel={t('settings.brandTerms.editorLabel')}
                    insertVariableLabel=""
                    translateFn={t}
                    variant="document"
                    showVariableControls={false}
                    isPreviewing={isPreviewing}
                    previewContent={preview}
                    toolbarTrailingItems={
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setIsPreviewing((value) => !value)}
                      >
                        {isPreviewing ? t('common.edit') : t('settings.brandTerms.preview')}
                      </Button>
                    }
                    editableMinH="320px"
                  />
                  {(save.isError || publish.isError) && (
                    <Alert.Root status="error">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>{t('settings.brandTerms.actionErrorTitle')}</Alert.Title>
                        <Alert.Description>
                          {t('settings.brandTerms.actionErrorDescription')}
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                  <HStack justify="flex-end" flexWrap="wrap">
                    <Button
                      variant="outline"
                      loading={save.isPending}
                      disabled={!title.trim() || save.isPending || publish.isPending}
                      onClick={() => void saveDraft()}
                    >
                      {save.isError ? t('common.retry') : t('settings.brandTerms.saveDraft')}
                    </Button>
                    <Button
                      colorPalette="primary"
                      loading={publish.isPending}
                      disabled={!savedDraft || dirty || save.isPending || publish.isPending}
                      onClick={() => void publishDraft()}
                    >
                      {t('settings.brandTerms.publish')}
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                displayedDocument && <RichTextContent data={displayedDocument.content} />
              )}
            </Modal>
          )}
        </VStack>
      )}
    </SettingsSection>
  );
};
